import * as React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, Location} from 'react-router-dom';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {Routing} from 'nutshell-core/routing';
import {SavedFilters} from 'nutshell-core/saved-filters';
import {Stagesets} from 'nutshell-core/stagesets';
import type {SavedFilter, SavedListType, CoreEntityType, Stageset} from 'nutshell-core/types';
import {getMultipleEntityType} from 'nutshell-core/utils/get-multiple-entity-type';

import {ButtonGroup} from 'shells/button-group/button-group';

import type {GetAudienceForEntityList_audience as Audience} from 'nutshell-graphql-types';

import {EntityListTitleBarButtons} from '../../../base/title-bar/entity-list-title-bar-buttons';
import {PageTitleBar} from '../../../layout';

import {
    isSavedListDirty,
    isAllEntitiesListDirty,
} from '../../../../saved-filters/saved-filters-utils';
import type {NutshellState} from '../../../../store';

import {getLeadsBoardViewDisplayName} from '../../entity-list-sidebar/helpers';
import type {ReduxSavedFilters} from '../../entity-list-sidebar/types';

import {SavedListsModal} from '../saved-lists-modal';

import {SubheaderPrimaryText} from './subheader-primary-text';

export type OwnProps = {
    entityType: CoreEntityType;
    entities: any;
    shouldShowExportAndEmailButtons: boolean;
    audience: Audience | null | undefined;
};
type StateProps = {
    activeSavedList: SavedFilter<SavedListType> | null | undefined;
    savedLists: ReduxSavedFilters;
    stagesets: Stageset[];
};
type RoutedProps = OwnProps & ContextRouter;
type Props = RoutedProps & StateProps;

type State = {
    isMyReportsModalOpen: boolean;
};

function mapStateToProps(state: NutshellState, ownProps: RoutedProps): StateProps {
    const activeSavedListId = getActiveSavedListFromLocation(ownProps.location);
    const savedLists = SavedFilters.getSavedLists(state);
    const activeSavedList = activeSavedListId ? savedLists[activeSavedListId] : null;
    const stagesets = Stagesets.getAll(state);

    return {
        activeSavedList,
        savedLists,
        stagesets,
    };
}

class EntityListTitleBarComponent extends React.PureComponent<Props, State> {
    // @ts-expect-error
    saveAnchor: HTMLElement;
    // @ts-expect-error
    saveAsAnchor: HTMLElement;
    // @ts-expect-error
    shareAnchor: HTMLElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            isMyReportsModalOpen: false,
        };
    }

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        const isDirty = getIsDirty(
            this.props.activeSavedList,
            this.props.audience,
            this.props.entityType,
            this.props.savedLists
        );

        return (
            <PageTitleBar
                title={
                    this.props.audience
                        ? this.getTitleForAudience(this.props.audience)
                        : this.getTitleForSavedList(this.props.activeSavedList)
                }
                titleTooltip={true}
                isModified={isDirty}
                subheaderPrimaryText={
                    this.props.audience
                        ? getSubheaderPrimaryTextAudience(this.props.audience)
                        : getSubheaderPrimaryTextList(
                              this.props.entityType,
                              this.props.activeSavedList,
                              isDirty
                          )
                }
                buttons={
                    <ButtonGroup>
                        <EntityListTitleBarButtons
                            entityType={this.props.entityType}
                            selectedCount={this.props.entities.selectedCount}
                            selectedItems={this.props.entities.selectedItems}
                            omittedItems={this.props.entities.omittedItems}
                            massSelect={this.props.entities.massSelect}
                            query={this.props.entities.query}
                            onEmailSend={this.props.entities.sendEmail}
                            exportSelectedItems={this.props.entities.exportSelectedItems}
                            activeSavedFilter={this.props.activeSavedList}
                            audience={this.props.audience}
                            shouldShowExportAndEmailButtons={
                                this.props.shouldShowExportAndEmailButtons
                            }
                        />
                        <SavedListsModal
                            isOpen={this.state.isMyReportsModalOpen}
                            entityType={this.props.entityType}
                            onClose={this.handleMyReportsModalClose}
                        />
                    </ButtonGroup>
                }
            />
        );
    }

    handleMyReportsButtonClick = () => {
        this.setState({isMyReportsModalOpen: true});
    };

    handleMyReportsModalClose = () => {
        this.setState({isMyReportsModalOpen: false});
    };

    getTitleForAudience(audience?: Audience | null) {
        let nameToDisplay = '';

        if (audience) {
            nameToDisplay = audience.name;
        } else {
            nameToDisplay = this.getDefaultEntityDisplayName();
        }

        return nameToDisplay;
    }

    getTitleForSavedList(savedFilter?: SavedFilter<SavedListType> | null) {
        let nameToDisplay = '';

        if (savedFilter) {
            nameToDisplay = savedFilter.name;
        } else {
            nameToDisplay = this.getDefaultEntityDisplayName();
        }

        return nameToDisplay;
    }

    getDefaultEntityDisplayName() {
        return getEntityDisplayName(this.props.entityType, this.props.stagesets);
    }
}

// @ts-expect-error
const connector = connect<Props, RoutedProps, _, _, _, _>(mapStateToProps);

// @ts-expect-error
export const EntityListTitleBar = withRouter<OwnProps>(connector(EntityListTitleBarComponent));

/**
 * Using a react-router location object, return the active saved list, if present
 * @param  {Object} location The location object from react-router
 * @return {string}          The currently active saved list id, if one is active
 */
function getActiveSavedListFromLocation(location: Location): string | null | undefined {
    const params = Routing.deparam(location.search.replace(/^\?/, ''));

    return params.list;
}

/**
 * Using a react-router location object, return the default list name for People, companies, and leads pages
 * @param  {CoreEntityType} entityType The entity type (ex. contacts, accounts, leads)
 * @return {string}          The default list name to display
 */
function getEntityDisplayName(entityType: CoreEntityType, stagesets: Stageset[]): string {
    if (entityType === 'leads') {
        const view = Routing.deparam(window.location.search.replace(/^\?/, '')).view;

        return view === 'board'
            ? getLeadsBoardViewDisplayName(window.location.search, stagesets, true)
            : 'All leads';
    }

    return `All ${getMultipleEntityType(entityType)}`;
}

/**
 * Simple pure function that returns a UI-ready string for the subheader
 * primary text given a certain entity type or based on the active saved list.
 *
 * @param {CoreEntityType} entityType The enum value to determine which kind
 *                                    of Nutshell entity we're dealing with
 * @param {SavedFilter<SavedListType>} activeSavedList The active saved list
 * @param {boolean} isDirty           Whether the active saved list is dirty
 * @return {string}                   A UI-read string
 */
function getSubheaderPrimaryTextList(
    entityType: CoreEntityType,
    activeSavedList: SavedFilter<SavedListType> | null | undefined,
    isDirty: boolean
): string | React.ReactNode {
    if (activeSavedList && activeSavedList.createdTime) {
        const visibility = activeSavedList.isShared ? 'Shared list' : 'Your list';

        return (
            <SubheaderPrimaryText
                visibility={visibility}
                description={activeSavedList.description}
                creatorUserName={activeSavedList.creatorUserName}
                createdTime={activeSavedList.createdTime}
            />
        );
    } else if (!activeSavedList && isDirty) {
        return 'Once you have selected all the filters you want for this list, save to pin it to the secondary navigation bar';
    }

    switch (entityType) {
        case 'leads':
            return 'Business deals with your companies and people';
        case 'contacts':
            return 'Contacts (or the individuals) you do business with';
        case 'accounts':
            return 'Accounts and organizations you do business with';
        default:
            return '';
    }
}

/**
 * Simple pure function that returns a UI-ready string for the subheader
 * primary text given a certain entity type or based on the active audience
 *
 * @param {Audience} activeAudience The active audience
 * @return {string}                   A UI-ready string
 */
function getSubheaderPrimaryTextAudience(activeAudience: Audience): string | React.ReactNode {
    if (activeAudience.createdTime) {
        const creatorName = activeAudience.creatorUser ? activeAudience.creatorUser.name : null;

        return (
            <SubheaderPrimaryText
                description={activeAudience.description}
                creatorUserName={creatorName}
                createdTime={activeAudience.createdTime}
            />
        );
    }

    return 'Contacts (or the individuals) you do business with';
}

function getIsDirty(
    activeSavedList: SavedFilter<SavedListType> | null | undefined,
    audience: Audience | null | undefined,
    entityType: CoreEntityType,
    savedLists: ReduxSavedFilters
) {
    // Audiences can't be dirty so only check dirty state if in saved list
    if (audience) {
        return false;
    }

    return activeSavedList
        ? // HACK: This relies on the fact that `props.activeSavedList` is not updated when changes are made
          // to filters.  The reason it isn't being updated right now is because the bacon code which
          // handles the filters does not use the same `history` object as react-router.
          isSavedListDirty(window.location.search, activeSavedList, entityType)
        : isAllEntitiesListDirty(window.location.search, entityType, savedLists);
}
